import i18n from '@/utils/i18n';
import React, { useEffect } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import { ScanCartButton } from './scan-cart-button';
import { ScannerType } from './scanner-types';
import { ScanPageContainer } from './scan-page-container';
import { ScanResultsSheet } from './scan-results-sheet';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useMaybeOrderDrawerContext } from '@/contexts/order-drawer-context';
import { useScannerContext } from '@/contexts/scanner-context';
import { View } from 'react-native';

export const CatalogScannerScreen: React.FC = () => {
  const {
    errorText,
    isOffDeviceSearchInProgress,
    onDeviceSearchResults,
    offDeviceSearchResults,
    searchAndUpdateOrderFn,
    syncCartVariantPacks,
    updateCartWithMultiUpdates,
  } = useScannerContext();
  const orderDrawerContext = useMaybeOrderDrawerContext();
  const [styles, theme] = useStyles();

  // Sync cart updates when closing order summary drawer on tablet
  useEffect(() => {
    if (orderDrawerContext && !orderDrawerContext.open) {
      syncCartVariantPacks();
    }
  }, [orderDrawerContext, syncCartVariantPacks]);

  // Sync cart updates when navigating back from order summary on mobile
  useComponentFocus({
    onFocus: syncCartVariantPacks,
  });

  const hideCameraButtons = Boolean(
    (onDeviceSearchResults && onDeviceSearchResults.length > 1) ||
      (offDeviceSearchResults && offDeviceSearchResults.length > 1),
  );

  return (
    <ScanPageContainer
      errorText={errorText}
      hideCameraButtons={hideCameraButtons}
      navigationHeaderProps={(scannerType) => ({
        actions: [
          {
            component: (
              <ScanCartButton
                fill={
                  scannerType === ScannerType.Camera
                    ? theme.sk.color.icon.ondark
                    : theme.sk.color.icon.primary
                }
                totalColor={
                  scannerType === ScannerType.Camera
                    ? theme.sk.color.text.ondark
                    : theme.sk.color.text.primary
                }
              />
            ),
          },
        ],
      })}
      onClearResults={updateCartWithMultiUpdates}
      scanInputProps={{
        bottomComponent:
          isOffDeviceSearchInProgress &&
          offDeviceSearchResults &&
          offDeviceSearchResults.length === 0 ? (
            <View style={styles.searchContainer}>
              <ActivityIndicator />
              <Typography
                color={theme.sk.color.text.tertiary}
                variant="paragraph-md"
              >
                {i18n.t('searching_the_catalog')}
              </Typography>
            </View>
          ) : null,
        scanInputClearable: true,
      }}
      searchByCode={searchAndUpdateOrderFn}
    >
      {(scannerType) => (
        <ScanResultsSheet
          containerStyle={[
            styles.base,
            scannerType === ScannerType.External
              ? styles.externalContainer
              : null,
          ]}
          scannerType={scannerType}
        />
      )}
    </ScanPageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  externalContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    marginTop: 4,
  },
  searchContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    height: 32,
    paddingLeft: 4,
  },
}));
